import Cookies from 'js-cookie'

export const AUTH_TOKEN_KEY = 'WEBSITE_TOKEN'
export const AUTH_USER_ID_KEY = 'WEBSITE_USERID'
export const DEVICE_ID_KEY = 'deviceId'
export const AUTH_COOKIE_DAYS = 15

export function parseCookieString(cookieString = '') {
    return cookieString.split('; ').reduce((result, item) => {
        const index = item.indexOf('=')
        if (index === -1) {
            return result
        }

        const key = item.slice(0, index)
        const value = item.slice(index + 1)
        result[key] = value
        return result
    }, {})
}

export function readAuthFromCookie(cookieString = '') {
    const cookies = parseCookieString(cookieString)
    return {
        token: cookies[AUTH_TOKEN_KEY] || '',
        userId: cookies[AUTH_USER_ID_KEY] || '',
        deviceId: cookies[DEVICE_ID_KEY] || '',
        cookieString,
    }
}

export function readAuthFromRequest(req) {
    const cookieString = req && req.headers ? req.headers.cookie || '' : ''
    return readAuthFromCookie(cookieString)
}

export function readBrowserAuth() {
    if (typeof window === 'undefined') {
        return { token: '', userId: '', deviceId: '' }
    }

    return {
        token: Cookies.get(AUTH_TOKEN_KEY) || '',
        userId: Cookies.get(AUTH_USER_ID_KEY) || '',
        deviceId: Cookies.get(DEVICE_ID_KEY) || '',
    }
}

export function getMainDomain(hostname) {
    if (!hostname) {
        return ''
    }

    const isIp = /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)
    if (isIp || hostname === 'localhost' || hostname.indexOf('.') === -1) {
        return ''
    }

    const parts = hostname.split('.')
    const secondLevelDomains = ['co', 'com', 'org', 'net', 'gov', 'edu']
    if (parts.length > 2 && secondLevelDomains.indexOf(parts[parts.length - 2]) > -1) {
        return parts.slice(-3).join('.')
    }

    return parts.slice(-2).join('.')
}

function getCookieOptions() {
    const options = {
        path: '/',
        sameSite: 'Lax',
    }

    if (typeof window !== 'undefined') {
        const domain = getMainDomain(window.location.hostname)
        if (domain) {
            options.domain = domain
        }
        if (window.location.protocol === 'https:') {
            options.secure = true
        }
    }

    return options
}

function setAuthCookie(name, value, days = AUTH_COOKIE_DAYS) {
    if (typeof window === 'undefined' || value === undefined || value === null) {
        return
    }

    Cookies.set(name, String(value), {
        ...getCookieOptions(),
        expires: days,
    })
}

function removeAuthCookie(name) {
    if (typeof window === 'undefined') {
        return
    }

    const options = getCookieOptions()
    Cookies.remove(name, options)
    Cookies.remove(name, { path: '/' })
}

export function setAuthSession(token, userId) {
    if (!token || !userId) {
        return
    }

    setAuthCookie(AUTH_TOKEN_KEY, token)
    setAuthCookie(AUTH_USER_ID_KEY, userId)
}

export function clearAuthSession() {
    removeAuthCookie(AUTH_TOKEN_KEY)
    removeAuthCookie(AUTH_USER_ID_KEY)
}

export function getOrCreateDeviceId() {
    if (typeof window === 'undefined') {
        return ''
    }

    let deviceId = Cookies.get(DEVICE_ID_KEY)
    if (!deviceId) {
        deviceId = new Date().getTime().toString(36) + Math.random().toString(36).slice(3)
        setAuthCookie(DEVICE_ID_KEY, deviceId, 365)
    }

    return deviceId
}

export function getExplorerInfo(userAgent = '') {
    const explorer = String(userAgent).toLowerCase()

    if (explorer.indexOf('msie') > -1) {
        return { type: 'IE', version: (explorer.match(/msie ([\d.]+)/) || [])[1] || 'Unknown' }
    }
    if (explorer.indexOf('firefox') > -1) {
        return { type: 'Firefox', version: (explorer.match(/firefox\/([\d.]+)/) || [])[1] || 'Unknown' }
    }
    if (explorer.indexOf('chrome') > -1) {
        return { type: 'Chrome', version: (explorer.match(/chrome\/([\d.]+)/) || [])[1] || 'Unknown' }
    }
    if (explorer.indexOf('opera') > -1) {
        return { type: 'Opera', version: (explorer.match(/opera.([\d.]+)/) || [])[1] || 'Unknown' }
    }
    if (explorer.indexOf('safari') > -1) {
        return { type: 'safari', version: (explorer.match(/version\/([\d.]+)/) || [])[1] || 'Unknown' }
    }

    return { type: 'Unknown', version: 'Unknown' }
}

export function getSystemVersion(userAgent = '') {
    const value = String(userAgent)
    if (/Windows NT 10|Windows 10/i.test(value)) return 'Win10'
    if (/Windows NT 6\.1|Windows 7/i.test(value)) return 'Win7'
    if (/Windows NT 6\.0|Windows Vista/i.test(value)) return 'WinVista'
    if (/Windows NT 5\.2|Windows 2003/i.test(value)) return 'Win2003'
    if (/Windows NT 5\.1|Windows XP/i.test(value)) return 'WinXP'
    if (/Windows NT 5\.0|Windows 2000/i.test(value)) return 'Win2000'
    if (/Macintosh|Mac OS X/i.test(value)) return 'Mac'
    if (/Linux/i.test(value)) return 'Linux'
    return 'other'
}

export function normalizeUserInfo(data, fallbackUserId) {
    const source = data || {}
    const userId = source.id || source.user_id || source.userId || fallbackUserId || ''
    const authenticateStatus = source.is_authenticate !== undefined
        ? Number(source.is_authenticate)
        : (source.isAuthenticated ? 1 : 0)

    return {
        ...source,
        id: userId,
        user_id: source.user_id || userId,
        userId: source.userId || userId,
        username: source.username || source.nickname || source.userName || '',
        poster: source.poster || source.avatar || source.userPoster || '',
        money: source.money !== undefined
            ? source.money
            : (source.coins !== undefined ? source.coins : 0),
        authenticateStatus,
        isAuthenticated: authenticateStatus === 1,
        hasBindWx: source.bindWeChat !== undefined
            ? Boolean(source.bindWeChat)
            : Boolean(source.hasBindWx || source.isBindWeChat),
    }
}
